import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Button, Chip,
  Skeleton, Fab, IconButton, Badge, FormControl, InputLabel, Select, MenuItem,
  TextField, Checkbox, FormControlLabel, Drawer, InputAdornment, Divider, Autocomplete, Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  CalendarToday,
  LocationOn,
  People,
  ArrowForward,
} from '@mui/icons-material';
import { eventsAPI, locationsAPI, organizersAPI, type Event, type EventFilters, type EventType } from '../services/api';
import { useAuthStore } from '../store/authStore';
import apiClient from '../services/api';

interface City { id: number; name: string; region: string | null; }
interface District { id: number; name: string; }
interface MetroStation { id: number; name: string; line_name: string; line_color: string; }

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [subscribedEvents, setSubscribedEvents] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<EventFilters>({ limit: 15 });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [typesExpanded, setTypesExpanded] = useState(false);
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<number>>(new Set());
  const [typesReady, setTypesReady] = useState(false);
  const organizerId = searchParams.get('organizer_id');
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [metroStations, setMetroStations] = useState<MetroStation[]>([]);
  const [organizerName, setOrganizerName] = useState<string | null>(null);
  const [selectedOrganizer, setSelectedOrganizer] = useState<any>(null);
  const [organizerSearchResults, setOrganizerSearchResults] = useState<any[]>([]);

  useEffect(() => {
    locationsAPI.getEventTypes().then(types => {
      setEventTypes(types);
      // Read type_ids from URL params (from HomePage)
      const urlTypeIds = searchParams.get('type_ids');
      const urlTypeId = searchParams.get('type_id');
      if (urlTypeIds) {
        const ids = urlTypeIds.split(',').map(Number).filter(n => !isNaN(n) && n > 0);
        if (ids.length > 0) {
          setSelectedTypeIds(new Set(ids));
          if (ids.length === 1) {
            setFilters(prev => ({ ...prev, type_id: ids[0] }));
          }
          setTypesReady(true);
          return;
        }
      } else if (urlTypeId) {
        const n = Number(urlTypeId);
        if (!isNaN(n) && n > 0) {
          setSelectedTypeIds(new Set([n]));
          setFilters(prev => ({ ...prev, type_id: n }));
          setTypesReady(true);
          return;
        }
      }
      setTypesReady(true);
    }).catch(() => { setEventTypes([]); setTypesReady(true); });
  }, []);

  useEffect(() => {
    const effectiveOrganizerId = organizerId || (filters.organizer_id ? String(filters.organizer_id) : null);
    if (effectiveOrganizerId) {
      apiClient.get(`/api/organizers/${effectiveOrganizerId}`)
        .then(r => {
          const org = r.data.organizer;
          setOrganizerName(org.name);
          setSelectedOrganizer({ id: org.id, first_name: org.name, last_name: '', username: org.username });
        })
        .catch(() => { setOrganizerName(null); setSelectedOrganizer(null); });
    } else {
      setOrganizerName(null);
      setSelectedOrganizer(null);
    }
  }, [organizerId, filters.organizer_id]);

  useEffect(() => {
    locationsAPI.getAllCities().then(setCities).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    if (filters.city_id) {
      locationsAPI.getDistricts(filters.city_id).then(setDistricts).catch(() => setDistricts([]));
      locationsAPI.getMetroStations(filters.city_id).then(setMetroStations).catch(() => setMetroStations([]));
    } else {
      setDistricts([]);
      setMetroStations([]);
    }
  }, [filters.city_id]);

  const loadEvents = async (reset = false) => {
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      const filtersWithOrganizer = organizerId
        ? { ...filters, organizer_id: Number(organizerId) }
        : filters;

      let allEvents: Event[] = [];
      let hasMoreResult = false;

      if (selectedTypeIds.size > 1 && reset) {
        // Multi-type: parallel requests per type, merge and deduplicate
        const promises = [...selectedTypeIds].map(typeId =>
          eventsAPI.getEvents({ ...filtersWithOrganizer, type_id: typeId, offset: 0, limit: 50 })
        );
        const results = await Promise.all(promises);
        const seen = new Set<number>();
        for (const r of results) {
          for (const ev of r.events) {
            if (!seen.has(ev.id)) { seen.add(ev.id); allEvents.push(ev); }
          }
        }
        allEvents.sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime());
        hasMoreResult = false;
      } else {
        const effectiveFilters = selectedTypeIds.size === 1
          ? { ...filtersWithOrganizer, type_id: [...selectedTypeIds][0] }
          : filtersWithOrganizer;
        const response = await eventsAPI.getEvents({ ...effectiveFilters, offset: currentOffset });
        allEvents = response.events;
        hasMoreResult = response.has_more;
      }

      if (reset) {
        setEvents(allEvents);
        const subs = new Set<number>();
        allEvents.forEach((ev: Event) => { if (ev.user_subscription) subs.add(ev.id); });
        setSubscribedEvents(subs);
      } else {
        setEvents(prev => [...prev, ...allEvents]);
      }
      setHasMore(hasMoreResult);
      setOffset(currentOffset + (allEvents.length || 0));
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (typesReady) loadEvents(true); }, [filters, typesReady]);

  const handleJoinEvent = async (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      await eventsAPI.joinEvent(eventId);
      loadEvents(true);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Ошибка при записи');
    }
  };

  const handleEventClick = (eventId: number) => navigate(`/events/${eventId}`);

  const getUserRoleBadge = (role: string) => {
    switch (role) {
      case 'participant': return { label: 'Участник', color: 'success' as const };
      case 'applicant': return { label: 'Заявка', color: 'warning' as const };
      default: return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const handleFilterChange = (key: keyof EventFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'type_id') {
      setSelectedTypeIds(() => {
        const next = new Set<number>();
        if (value !== undefined) next.add(value as number);
        return next;
      });
    }
  };

  const handleToggleType = (typeId: number) => {
    setSelectedTypeIds(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  const handleApplyTypeFilter = () => {
    if (selectedTypeIds.size === 1) {
      setFilters(f => ({ ...f, type_id: [...selectedTypeIds][0] }));
    } else {
      setFilters(f => ({ ...f, type_id: undefined }));
    }
  };

  const applyFilters = () => {
    setFilterPanelOpen(false);
    loadEvents(true);
  };

  const resetFilters = () => {
    setFilters({ limit: 15 });
    setFilterPanelOpen(false);
    loadEvents(true);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.type_id) count++;
    if (filters.city_id) count++;
    if (filters.district_id) count++;
    if (filters.metro_station_id) count++;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    if (filters.price_min !== undefined) count++;
    if (filters.price_max !== undefined) count++;
    if (filters.with_free_slots) count++;
    if (filters.status_filter) count++;
    if (filters.organizer_id) count++;
    return count;
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {organizerId ? (
              <>
                <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  Мероприятия организатора
                </Typography>
                {organizerName && (
                  <Typography variant="body2" color="primary" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                    {organizerName}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                Мероприятия
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterPanelOpen(true)}
              sx={{ position: 'relative', fontSize: '0.8rem', px: { xs: 1.5, sm: 2 } }}
              size="small"
            >
              Фильтры
              {getActiveFiltersCount() > 0 && (
                <Badge badgeContent={getActiveFiltersCount()} color="error" sx={{ position: 'absolute', top: -6, right: -6 }} />
              )}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { if (!isAuthenticated) { navigate('/login'); return; } navigate('/events/create'); }}
              sx={{ fontSize: '0.8rem', px: { xs: 1.5, sm: 2 }, bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}
              size="small"
            >
              Создать
            </Button>
          </Box>
        </Box>

        {/* Event type chips — top 5 + Прочее */}
        {eventTypes.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
                alignItems: 'center',
                pb: 0.5,
              }}
            >
              <Chip
                label="Все"
                size="small"
                onClick={() => { setSelectedTypeIds(new Set()); handleFilterChange('type_id', undefined); }}
                color={selectedTypeIds.size === 0 && !filters.type_id ? 'primary' : 'default'}
                variant={selectedTypeIds.size === 0 && !filters.type_id ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
              {eventTypes.slice(0, 5).map(type => (
                <Chip
                  key={type.id}
                  label={`${type.icon_emoji || ''} ${type.name}`}
                  size="small"
                  onClick={() => { handleToggleType(type.id); setTimeout(handleApplyTypeFilter, 0); }}
                  color={selectedTypeIds.has(type.id) ? 'primary' : 'default'}
                  variant={selectedTypeIds.has(type.id) ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              ))}
              {eventTypes.length > 5 && (
                <Chip
                  label={typesExpanded ? 'Свернуть' : `Прочее (${eventTypes.length - 5})`}
                  size="small"
                  onClick={() => setTypesExpanded(!typesExpanded)}
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                />
              )}
            </Box>
            <Collapse in={typesExpanded}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {eventTypes.slice(5).map(type => (
                  <Chip
                    key={type.id}
                    label={`${type.icon_emoji || ''} ${type.name}`}
                    size="small"
                    onClick={() => { handleToggleType(type.id); setTimeout(handleApplyTypeFilter, 0); }}
                    color={selectedTypeIds.has(type.id) ? 'primary' : 'default'}
                    variant={selectedTypeIds.has(type.id) ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Quick status filter */}
        <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
          {[
            { value: '', label: 'Все' },
            { value: 'available', label: 'Доступные' },
            { value: 'participant', label: 'Я участвую' },
            { value: 'organizer', label: 'Мои' },
          ].map(s => (
            <Chip
              key={s.value}
              label={s.label}
              size="small"
              onClick={() => handleFilterChange('status_filter', s.value || undefined)}
              color={filters.status_filter === (s.value || undefined) ? 'primary' : 'default'}
              variant={filters.status_filter === (s.value || undefined) ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}
            />
          ))}
        </Box>
      </Box>

      {/* Events list */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {events.map((event) => {
          const userRoleBadge = getUserRoleBadge(event.user_role);
          const participantsCount = event.reserved_seats + event.current_participants;
          const isFull = participantsCount >= event.max_participants;

          return (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: '#bdbdbd' },
                  ...(event.user_role !== 'guest' && {
                    borderLeft: '3px solid',
                    borderColor:
                      event.user_role === 'organizer' ? '#1565c0' :
                      event.user_role === 'participant' ? '#2e7d32' :
                      '#ef6c00',
                  }),
                }}
                onClick={() => handleEventClick(event.id)}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 }, pb: '0 !important' }}>
                  {/* Top badges */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={event.status === 'open' ? 'Набор открыт' : event.status === 'closed' ? 'Набор завершен' : 'Отменен'}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.65rem', fontWeight: 600,
                        ...(event.status === 'open' && { bgcolor: '#e8f5e9', color: '#2e7d32' }),
                        ...(event.status === 'closed' && { bgcolor: '#fff3e0', color: '#ef6c00' }),
                        ...(event.status === 'cancelled' && { bgcolor: '#f5f5f5', color: '#616161' }),
                      }}
                    />
                    <Chip
                      label={event.is_public ? 'Открытое' : 'Закрытое'}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20, fontSize: '0.65rem',
                        ...(event.is_public ? { borderColor: '#2e7d32', color: '#2e7d32' } : { borderColor: '#ef6c00', color: '#ef6c00' }),
                      }}
                    />
                    {userRoleBadge && <Chip label={userRoleBadge.label} size="small" color={userRoleBadge.color} sx={{ height: 20, fontSize: '0.65rem' }} />}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.3,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      mb: 0.5,
                    }}
                  >
                    {event.sport_type_icon || '🎯'} {event.title}
                  </Typography>

                  {/* Organizer */}
                  {event.organizer_display_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {event.organizer_display_name}
                    </Typography>
                  )}

                  {/* Compact info row */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 14, color: '#999' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.start_datetime)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14, color: '#999' }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {event.city}{event.metro_station ? `, м. ${event.metro_station}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 14, color: '#999' }} />
                        <Typography variant="caption" color="text.secondary">
                          {participantsCount}/{event.max_participants}
                        </Typography>
                      </Box>
                      {event.price > 0 && (
                        <Typography variant="caption" fontWeight={600} color="text.primary">
                          {event.price} ₽
                        </Typography>
                      )}
                      {event.price === 0 && (
                        <Chip label="Бесплатно" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                      )}
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: { xs: 1, sm: 1.5 }, pt: 0.5 }}>
                  {event.user_role === 'organizer' ? (
                    <Button size="small" variant="contained" fullWidth onClick={e => { e.stopPropagation(); handleEventClick(event.id); }}
                      sx={{ fontSize: '0.75rem', py: 0.5, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                      Управление
                    </Button>
                  ) : event.user_role === 'participant' ? (
                    <Button size="small" variant="outlined" color="error" fullWidth onClick={e => { e.stopPropagation(); handleEventClick(event.id); }}
                      sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      Отменить запись
                    </Button>
                  ) : event.user_role === 'applicant' ? (
                    <Button size="small" variant="outlined" color="warning" fullWidth onClick={e => { e.stopPropagation(); handleEventClick(event.id); }}
                      sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      Отменить заявку
                    </Button>
                  ) : isFull ? (
                    (event.user_subscription || subscribedEvents.has(event.id)) ? (
                      <Button size="small" variant="outlined" fullWidth disabled sx={{ fontSize: '0.75rem', py: 0.5 }}>
                        Уведомления включены
                      </Button>
                    ) : (
                      <Button size="small" variant="outlined" fullWidth onClick={async e => {
                        e.stopPropagation();
                        try { await eventsAPI.subscribeToEvent(event.id); setSubscribedEvents(prev => new Set(prev).add(event.id)); } catch {}
                      }} sx={{ fontSize: '0.75rem', py: 0.5 }}>
                        Уведомить о местах
                      </Button>
                    )
                  ) : event.can_join ? (
                    <Button size="small" variant="contained" fullWidth onClick={e => handleJoinEvent(event.id, e)}
                      sx={{ fontSize: '0.75rem', py: 0.5, bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>
                      {event.is_public ? 'Записаться' : 'Подать заявку'}
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" fullWidth disabled sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      Регистрация закрыта
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}

        {/* Loading skeletons */}
        {loading && events.length === 0 && (
          <>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ height: 180 }}>
                  <CardContent>
                    <Skeleton variant="rectangular" height={18} width="60%" sx={{ mb: 1 }} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="40%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        )}
      </Grid>

      {/* Load more */}
      {!loading && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" size="medium" onClick={() => loadEvents()} startIcon={<ArrowForward />}>
            Показать ещё
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {!loading && events.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1rem' }}>
            Мероприятий не найдено
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Попробуйте изменить фильтры или создайте мероприятие
          </Typography>
          <Button
            variant="contained"
            onClick={() => { if (!isAuthenticated) { navigate('/login'); return; } navigate('/events/create'); }}
            startIcon={<AddIcon />}
            sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}
          >
            Создать мероприятие
          </Button>
        </Box>
      )}

      {/* FAB mobile */}
      <Fab
        color="primary"
        aria-label="create"
        onClick={() => { if (!isAuthenticated) { navigate('/login'); return; } navigate('/events/create'); }}
        sx={{
          position: 'fixed',
          bottom: 72,
          right: 16,
          display: { xs: 'flex', md: 'none' },
          bgcolor: '#ef6c00',
          '&:hover': { bgcolor: '#e65100' },
          width: 48,
          height: 48,
        }}
      >
        <AddIcon />
      </Fab>

      {/* Filter drawer */}
      <Drawer
        anchor="right"
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 360, maxWidth: '90vw' } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Фильтры</Typography>
            <IconButton onClick={() => setFilterPanelOpen(false)} size="small"><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <FormControl fullWidth sx={{ mb: 2 }} size="small">
              <InputLabel>Статус</InputLabel>
              <Select value={filters.status_filter || ''} label="Статус" onChange={e => handleFilterChange('status_filter', e.target.value)}>
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="available">Доступные</MenuItem>
                <MenuItem value="applicant">Мои заявки</MenuItem>
                <MenuItem value="participant">Я участвую</MenuItem>
                <MenuItem value="organizer">Мои</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              fullWidth
              options={eventTypes}
              getOptionLabel={option => `${option.icon_emoji || ''} ${option.name}`}
              renderInput={params => <TextField {...params} label="Вид мероприятия" size="small" />}
              value={eventTypes.find(t => t.id === filters.type_id) || null}
              onChange={(_, v) => handleFilterChange('type_id', v?.id || undefined)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText="Не найдено"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>ОРГАНИЗАТОР</Typography>

            <Autocomplete
              fullWidth
              freeSolo
              options={organizerSearchResults}
              filterOptions={o => o}
              getOptionLabel={option => {
                if (typeof option === 'string') return option;
                if ((option as any)._notFound) return '';
                const name = `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username || '';
                return name;
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={params => (
                <TextField {...params} label="Организатор" size="small" helperText="Введите имя, фамилию, логин или код организатора" />
              )}
              onInputChange={async (_, value, reason) => {
                if (reason === 'reset' && !value) { setOrganizerSearchResults([]); handleFilterChange('organizer_id', undefined); return; }
                if (value.length >= 4) {
                  try {
                    const data = await organizersAPI.search(value);
                    setOrganizerSearchResults((data.users || []).length === 0 ? [{ id: -1, _notFound: true } as any] : data.users);
                  } catch { setOrganizerSearchResults([{ id: -1, _notFound: true } as any]); }
                } else { setOrganizerSearchResults([]); }
              }}
              onChange={(_, v) => {
                if (v && typeof v !== 'string' && !(v as any)._notFound) { setSelectedOrganizer(v); handleFilterChange('organizer_id', v.id); }
                else if (!v) { setSelectedOrganizer(null); handleFilterChange('organizer_id', undefined); }
              }}
              value={selectedOrganizer}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>МЕСТОПОЛОЖЕНИЕ</Typography>

            <Autocomplete fullWidth options={cities}
              getOptionLabel={o => `${o.name} ${o.region ? `(${o.region})` : ''}`}
              renderInput={params => <TextField {...params} label="Город" size="small" />}
              value={cities.find(c => c.id === filters.city_id) || null}
              onChange={(_, v) => handleFilterChange('city_id', v?.id || undefined)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText="Не найдено"
              sx={{ mb: 2 }}
            />

            <Autocomplete fullWidth options={districts}
              getOptionLabel={o => o.name}
              disabled={!filters.city_id || districts.length === 0}
              renderInput={params => <TextField {...params} label="Район" size="small" />}
              value={districts.find(d => d.id === filters.district_id) || null}
              onChange={(_, v) => handleFilterChange('district_id', v?.id || undefined)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText="Нет районов"
              sx={{ mb: 2 }}
            />

            <Autocomplete fullWidth options={metroStations}
              getOptionLabel={o => o.name}
              disabled={!filters.city_id || metroStations.length === 0}
              filterOptions={(opts, st) => {
                const inp = st.inputValue.toLowerCase().trim();
                return inp ? opts.filter(o => o.name.toLowerCase().includes(inp)) : opts;
              }}
              renderOption={(props, station) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={station.id} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 3, bgcolor: station.line_color || '#999', borderRadius: 1, flexShrink: 0 }} />
                    <Typography variant="body2" noWrap>{station.name}</Typography>
                  </Box>
                );
              }}
              renderInput={params => <TextField {...params} label="Метро" size="small" />}
              value={metroStations.find(s => s.id === filters.metro_station_id) || null}
              onChange={(_, v) => handleFilterChange('metro_station_id', v?.id || undefined)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText={metroStations.length === 0 ? "Выберите город" : "Не найдено"}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>ДАТА</Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <TextField fullWidth label="От" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={filters.date_from || ''} onChange={e => handleFilterChange('date_from', e.target.value)} />
              <TextField fullWidth label="До" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={filters.date_to || ''} onChange={e => handleFilterChange('date_to', e.target.value)} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <TextField fullWidth label="Время от" type="time" size="small" InputLabelProps={{ shrink: true }}
                value={filters.time_from || ''} onChange={e => handleFilterChange('time_from', e.target.value)} />
              <TextField fullWidth label="Время до" type="time" size="small" InputLabelProps={{ shrink: true }}
                value={filters.time_to || ''} onChange={e => handleFilterChange('time_to', e.target.value)} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>СТОИМОСТЬ</Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <TextField fullWidth label="От" type="number" size="small" value={filters.price_min || ''}
                onChange={e => handleFilterChange('price_min', e.target.value ? Number(e.target.value) : undefined)}
                InputProps={{ endAdornment: <InputAdornment position="end">₽</InputAdornment> }} inputProps={{ min: 0 }} />
              <TextField fullWidth label="До" type="number" size="small" value={filters.price_max || ''}
                onChange={e => handleFilterChange('price_max', e.target.value ? Number(e.target.value) : undefined)}
                InputProps={{ endAdornment: <InputAdornment position="end">₽</InputAdornment> }} inputProps={{ min: 0 }} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={<Checkbox checked={filters.with_free_slots || false} onChange={e => handleFilterChange('with_free_slots', e.target.checked ? true : undefined)} />}
              label={<Typography variant="body2">Свободные места</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={filters.is_public || false} onChange={e => handleFilterChange('is_public', e.target.checked ? true : undefined)} />}
              label={<Typography variant="body2">Только открытые</Typography>}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" fullWidth onClick={resetFilters} size="small">Сбросить</Button>
            <Button variant="contained" fullWidth onClick={applyFilters} size="small"
              sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>
              Применить
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default EventsPage;